import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {VStack} from 'native-base';
import {Input, Radio, Table} from 'antd';
import filter from 'lodash/filter';
import {t} from '../../../../services/i18n';
import {fetchApi} from '../../../../services/HTTP';
import AdvanceSelect from '../../../common/AdvanceSelect';
import CustomButtonV2 from '../../../common/CustomButtonV2';
import {Colors} from '../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../styles/GlobalStyles';
import FormInput from '../../../common/FormInput';
import AppText from '../../../common/AppText';
import {CustomerManagementType} from '../../../../Configs/DatabaseConsts';

const TABLE_FONT_SIZE = GlobalStyleAttributes.fonts.standard16;
const CUSTOMER_CARD_DEFAULT_ACCOUNT = '1200';
const DEFAULT_ACCOUNT_NUMBER = '9999';
const sanitizeLetterOrNumberCode = (value, maxLength = 3) => (
    String(value ?? '').replace(/[^\p{L}\p{N}]/gu, '').slice(0, maxLength)
);

const serializeTransactionTypes = (rows = []) => JSON.stringify(
    rows.map((row) => ({
        type_key: row.type_key,
        code: row.code ?? '',
    })),
);

const ACCOUNT_CARD_SECTIONS = [
    {
        key: 'income',
        titleKey: 'account-cards-section-income',
        types: ['revenue', 'vat'],
        showAddButton: true,
    },
    {
        key: 'receipts',
        titleKey: 'account-cards-section-receipts',
        types: ['receipt'],
    },
];

const tableCellProps = () => ({style: {fontSize: TABLE_FONT_SIZE}});

const AccountCardsSettings = ({values, setFieldValue, errors, touched}) => {
    const [loading, setLoading] = useState(false);

    const fetchCards = useCallback(async (locationId) => {
        setLoading(true);
        try {
            const query = locationId ? `?locationId=${locationId}` : '';
            const res = await fetchApi(`finance/accountCards${query}`, 'GET', null, false, true);
            const cards = res.cards || [];
            setFieldValue('locationId', res.locationId);
            setFieldValue('accounting_separation', res.accounting_separation);
            setFieldValue('location_options', res.location_options || []);
            setFieldValue('cards', cards);
            setFieldValue('customer_management_type', res.customer_management_type ?? CustomerManagementType.CONSOLIDATED);
            setFieldValue('initial_customer_management_type', res.customer_management_type ?? CustomerManagementType.CONSOLIDATED);
            setFieldValue('has_foreign_numbers', res.has_foreign_numbers ?? false);
            setFieldValue('show_branch_customer_cards', res.show_branch_customer_cards ?? false);
            setFieldValue('branch_account_numbers', res.branch_account_numbers || []);
            const transactionTypes = (res.transaction_types || []).map((row) => ({...row, is_edited: false}));
            setFieldValue('transaction_types', transactionTypes);
            setFieldValue('initial_transaction_types', transactionTypes.map((row) => ({...row})));
        } finally {
            setLoading(false);
        }
    }, [setFieldValue]);

    useEffect(() => {
        fetchCards(values.locationId);
    }, []);

    const isConsolidated = values.customer_management_type === CustomerManagementType.CONSOLIDATED;
    const showBranchCustomerCards = isConsolidated && values.show_branch_customer_cards;
    const showSingleCustomerCard = isConsolidated && !values.show_branch_customer_cards;

    const hasUnsavedChanges = useMemo(
        () => values.cards.filter(card => {
            if ((card.type || '').toLowerCase() === 'customer' && !showSingleCustomerCard) {
                return false;
            }
            return card.is_new || card.is_edited;
        }).length > 0
            || values.customer_management_type !== values.initial_customer_management_type
            || (showBranchCustomerCards && (values.branch_account_numbers || []).some(row => row.is_edited))
            || serializeTransactionTypes(values.transaction_types) !== serializeTransactionTypes(values.initial_transaction_types),
        [values.cards, values.customer_management_type, values.initial_customer_management_type, values.branch_account_numbers, values.transaction_types, values.initial_transaction_types, showBranchCustomerCards, showSingleCustomerCard],
    );

    const onLocationChange = (option) => {
        if (hasUnsavedChanges) return;
        const locationId = option?.value ?? null;
        setFieldValue('locationId', locationId);
        fetchCards(locationId);
    };

    const getCardIndex = (cardId) => (values.cards || []).findIndex((card) => card.id === cardId);

    const getSectionCards = (types) => filter(values.cards || [], (card) => types.includes((card.type || '').toLowerCase()));

    const updateCardField = (cardId, field, value) => {
        const cards = (values.cards || []).map(card => {
            if (card.id !== cardId) return card;
            const updatedCard = {...card, [field]: value};
            if (!card.is_new) {
                updatedCard.is_edited = true;
            }
            return updatedCard;
        });
        setFieldValue('cards', cards);
    };

    const updateBranchAccountNumber = (branchId, accountNumber) => {
        const branchAccountNumbers = (values.branch_account_numbers || []).map(row => (
            row.branch_id === branchId ? {...row, account_number: accountNumber, is_edited: true} : row
        ));
        setFieldValue('branch_account_numbers', branchAccountNumbers);
    };

    const updateTransactionTypeCode = (typeKey, code) => {
        const sanitizedCode = sanitizeLetterOrNumberCode(code, 3);
        const transactionTypes = (values.transaction_types || []).map((row) => (
            row.type_key === typeKey
                ? {...row, code: sanitizedCode || null, is_edited: true}
                : row
        ));
        setFieldValue('transaction_types', transactionTypes);
    };

    const getTransactionTypeIndex = (typeKey) => (values.transaction_types || []).findIndex((row) => row.type_key === typeKey);

    const handleDeleteNewCard = (cardId) => {
        setFieldValue('cards', (values.cards || []).filter(card => card.id !== cardId));
    };

    const handleAddIncomeCardClick = () => {
        const length = values.cards?.length;
        setFieldValue('cards', [
            ...(values.cards || []),
            {
                name: '',
                account_number: '',
                type: 'revenue',
                id: length ? `new_${length + 1}` : 'new_1',
                is_new: true,
            },
        ]);
    };

    const columns = [
        {
            title: t('account-card-name'),
            dataIndex: 'name',
            key: 'name',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (name, record) => (record.is_new || record.is_editable) ? (
                <Input
                    value={name ?? ''}
                    onChange={(e) => updateCardField(record.id, 'name', e.target.value)}
                    placeholder={t('income-card-name')}
                    status={touched?.cards?.[getCardIndex(record.id)]?.name && errors?.cards?.[getCardIndex(record.id)]?.name ? 'error' : undefined}
                />
            ) : name,
        },
        {
            title: t('account-number'),
            dataIndex: 'account_number',
            key: 'account_number',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (accountNumber, record) => {
                const isCustomerCard = (record.type || '').toLowerCase() === 'customer';
                return (
                    <Input
                        type="text"
                        inputMode="text"
                        value={accountNumber ?? ''}
                        onChange={(e) => updateCardField(record.id, 'account_number', e.target.value)}
                        placeholder={isCustomerCard ? CUSTOMER_CARD_DEFAULT_ACCOUNT : DEFAULT_ACCOUNT_NUMBER}
                    />
                );
            },
        },
        {
            key: 'actions',
            width: 50,
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (_, record) => record.is_new ? (
                <CustomButtonV2
                    onClick={() => handleDeleteNewCard(record.id)}
                    iconName={'fa-trash-can'}
                    type={'link'}
                />
            ) : null,
        },
    ];

    const branchAccountColumns = [
        {
            title: t('location'),
            dataIndex: 'branch_name',
            key: 'branch_name',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
        },
        {
            title: t('account-number'),
            dataIndex: 'account_number',
            key: 'account_number',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (accountNumber, record) => (
                <Input
                    type="text"
                    inputMode="text"
                    value={accountNumber ?? ''}
                    onChange={(e) => updateBranchAccountNumber(record.branch_id, e.target.value)}
                    placeholder={CUSTOMER_CARD_DEFAULT_ACCOUNT}
                />
            ),
        },
    ];

    const transactionTypeColumns = [
        {
            title: t('account-cards-document-type'),
            dataIndex: 'display_name',
            key: 'display_name',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
        },
        {
            title: t('account-cards-document-type-code'),
            dataIndex: 'code',
            key: 'code',
            onCell: tableCellProps,
            onHeaderCell: tableCellProps,
            render: (code, record) => {
                const rowIndex = getTransactionTypeIndex(record.type_key);
                const hasError = touched?.transaction_types?.[rowIndex]?.code && errors?.transaction_types?.[rowIndex]?.code;

                return (
                    <Input
                        type="text"
                        inputMode="text"
                        data-testid={`account-cards-transaction-type-code-${record.type_key}`}
                        value={code ?? ''}
                        maxLength={3}
                        onChange={(e) => updateTransactionTypeCode(record.type_key, e.target.value)}
                        placeholder="999"
                        status={hasError ? 'error' : undefined}
                    />
                );
            },
        },
    ];

    return (
        <VStack space={'1.5rem'} flex={1}>
            {values.accounting_separation && (
                <FormInput label={t('account-cards-branch-select')} width={'100%'} containerProps={{mb: 0}}>
                    <AdvanceSelect
                        dontSort
                        options={values.location_options || []}
                        initValue={values.locationId}
                        onChange={onLocationChange}
                        isDisabled={hasUnsavedChanges}
                    />
                </FormInput>
            )}
            {ACCOUNT_CARD_SECTIONS.map((section) => {
                const sectionCards = getSectionCards(section.types);

                return (
                    <VStack key={section.key} space={'0.5rem'}>
                        <AppText variant={GlobalStyleAttributes.textVariants.heading2}>
                            {t(section.titleKey)}
                        </AppText>
                        <Table
                            loading={loading}
                            dataSource={sectionCards.map(card => ({...card, key: card.id}))}
                            columns={columns}
                            pagination={false}
                            size="middle"
                        />
                        {section.showAddButton && (
                            <CustomButtonV2
                                type={'link'}
                                onClick={handleAddIncomeCardClick}
                                wrapperStyle={{width: 'max-content'}}
                                text={`+ ${t('add-income-card')}`}
                                color={Colors.newDesign.mainBlue}
                                textProps={{color: Colors.newDesign.mainBlue, fontSize: GlobalStyleAttributes.fonts.heading4}}
                            />
                        )}
                    </VStack>
                );
            })}

            <VStack space={'0.75rem'}>
                <AppText variant={GlobalStyleAttributes.textVariants.heading2}>
                    {t('customer-management-settings')}
                </AppText>
                <Radio.Group
                    value={values.customer_management_type}
                    onChange={(e) => setFieldValue('customer_management_type', e.target.value)}
                >
                    <VStack space={'0.5rem'}>
                        <Radio value={CustomerManagementType.CONSOLIDATED}>{t('customer-management-consolidated')}</Radio>
                        <Radio value={CustomerManagementType.INDIVIDUAL}>{t('customer-management-individual')}</Radio>
                    </VStack>
                </Radio.Group>

                {showSingleCustomerCard && (
                    <VStack space={'0.5rem'}>
                        <AppText variant={GlobalStyleAttributes.textVariants.heading2}>
                            {t('account-cards-section-customer')}
                        </AppText>
                        <Table
                            loading={loading}
                            dataSource={getSectionCards(['customer']).map(card => ({...card, key: card.id}))}
                            columns={columns}
                            pagination={false}
                            size="middle"
                        />
                    </VStack>
                )}

                {showBranchCustomerCards && (
                    <VStack space={'0.5rem'}>
                        <AppText variant={GlobalStyleAttributes.textVariants.heading2}>
                            {t('account-cards-section-customer')}
                        </AppText>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText}>
                            {t('customer-management-consolidated-location-hint')}
                        </AppText>
                        <Table
                            dataSource={(values.branch_account_numbers || []).map(row => ({...row, key: row.branch_id}))}
                            columns={branchAccountColumns}
                            pagination={false}
                            size="middle"
                        />
                    </VStack>
                )}

                {values.customer_management_type === CustomerManagementType.INDIVIDUAL && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.infoText}>
                        {t('customer-management-individual-hint')}
                    </AppText>
                )}
            </VStack>

            <VStack space={'0.5rem'} data-testid="account-cards-transaction-types-section">
                <AppText variant={GlobalStyleAttributes.textVariants.heading2}>
                    {t('account-cards-document-types')}
                </AppText>
                <Table
                    loading={loading}
                    dataSource={(values.transaction_types || []).map((row) => ({...row, key: row.type_key}))}
                    columns={transactionTypeColumns}
                    pagination={false}
                    size="middle"
                />
            </VStack>
        </VStack>
    );
};

export default AccountCardsSettings;
